import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import enCommon from './locales/en.json'
import frCommon from './locales/fr.json'

/** Locale codes msgban currently supports. Adding a new one is two steps:
 *   1. Create the JSON file at `./locales/<code>.json` (translate from en.json)
 *   2. Add it to SUPPORTED_LOCALES + the resources object below.
 *  The Profile modal's language dropdown also reads from SUPPORTED_LOCALES. */
export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]['code']

/** Default fallback when:
 *   - the user hasn't picked a language yet,
 *   - the detected browser language isn't in SUPPORTED_LOCALES,
 *   - a translation key is missing in the active locale.
 *  Set to 'en' because msgban targets the international market first
 *  (WhatsApp Business is more mature outside FR/CH). The browser-language
 *  detector still picks fr-FR users in FR — this is only a fallback. */
export const DEFAULT_LOCALE: SupportedLocale = 'en'

/** Resolve a user-preference locale string to a supported one, falling
 *  back to DEFAULT_LOCALE if unknown. Used by the Board to push the
 *  user's saved choice into i18next on mount. */
export function resolveLocale(input: string | null | undefined): SupportedLocale {
  if (!input) return DEFAULT_LOCALE
  const code = input.split('-')[0].toLowerCase()
  return (SUPPORTED_LOCALES.find((l) => l.code === code)?.code ??
    DEFAULT_LOCALE) as SupportedLocale
}

i18n
  // Browser language detection is layered ON TOP of the user-preference
  // override. On first load (before the user is logged in), i18next picks
  // their browser language if it's in SUPPORTED_LOCALES, else DEFAULT_LOCALE.
  // Once the user logs in, App.tsx reads `userPrefs.locale` and calls
  // `i18n.changeLanguage(locale)` to switch to their saved choice.
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enCommon },
      fr: { translation: frCommon },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
    // Only consider the 2-letter primary code (so en-US, en-GB, en-AU
    // all map to "en" not to themselves — we don't have regional variants).
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'msgban-locale',
    },
    interpolation: {
      // React already escapes strings, so i18next doesn't need to.
      escapeValue: false,
    },
    returnNull: false,
  })

export default i18n
